// collection-data.ts
// Reads the CMS values a page requests, and applies them to the markup.

import Base from '../base/base';
import { API_BASE } from '../../shared/utils/variables';
import {
  APPLIERS,
  APPLIER_NAMES,
  ApplierName,
  CONTEXT_ATTRIBUTE,
  ITEM_ATTRIBUTE,
  CollectionPayload,
  PathMeta,
  Timing,
  Unresolved,
  CmsResults,
  attributeOf,
  buildContext,
  contextRefsOf,
  depthOf,
  entriesOf,
  isEmpty,
  highlightFor,
  readPath
} from './collection-data-hooks';
import { LegendEntry, renderLegend } from './collection-data-legend';

interface Binding {
  node: HTMLElement;
  applier: ApplierName;
  path: string;
  /** For an imginject entry before `[~]`: the child its items precede. */
  anchor?: ChildNode | null;
}

/**
 * One request's worth of work: a source, the item it names,
 * the context its selectors ride on, and the bindings waiting on the
 * result. Two elements under different context elements query the same
 * source for different things, so both the slug and the context sit in
 * the grouping, not just the path.
 */
interface QueryGroup {
  source: string;
  /**
   * The item the source names, from the nearest context element that
   * declares it.
   */
  slug: string;
  context: Record<string, string>;
  bindings: Binding[];
}

const COLLECTION_DATA_API = `${API_BASE}/api/collection-data`;

interface CollectionDataOptions {
  baseUrl?: string;
  debug?: boolean;
  [key: string]: any;
}

/**
 * CollectionData
 *
 * A Keylight listing holds no bedroom count. It points at an elevation, which
 * points at a base, and only the base holds the number. An element names
 * which one it wants with a path:
 *
 *     <span data-cms-ref="listing.home-plan.base-home-plan.beds-2">3</span>
 *     <img data-cms-img="listing.home-plan.base-home-plan.interior-test" src="…">
 *     <div data-cms-bgimg="listing.home-plan.hero-image"></div>
 *
 * A plural source queries a whole collection instead of one item:
 *
 *     <span data-cms-ref="listings[neighborhood-link=neighborhood]:length">12</span>
 *
 * Every alias — the source a path starts on, and the one a selector's
 * filter value names — reads its item off the nearest context element
 * carrying
 * `data-cms-context="neighborhood" data-cms-item="islandview"` — two
 * attributes, because Webflow binds a whole value or none of it. A
 * template page writes the pair once on its body, and an index page
 * writes one on each card, so a card's counters and a card's fields both
 * speak for that card's item.
 *
 * This finds every such element, queries the API for those paths in one
 * request per source, item, and context, and writes each result in. The API walks the
 * references, so the browser makes one request however deep the chain runs.
 *
 * Nothing happens unless the page carries one of these attributes, so a page
 * without them costs no request.
 *
 * Whatever cannot be resolved is left exactly as Webflow rendered it — an
 * unset reference, a field this version cannot carry, a request that fails.
 * The markup in the attribute's element is the fallback, so the page reads
 * correctly either way. Run with `{ debug: true }` to see each miss and why,
 * or with `?__debug__` on the page URL to outline every image a reference
 * filled, one color per depth, name each source in a corner legend, add
 * `cms-debug` to the body, and print what each request cost.
 */
export default class CollectionData extends Base {
  /** Resolves when every source the page named has responded. */
  readonly ready: Promise<void>;

  private baseUrl: string;
  private fresh: boolean;
  private outlines: boolean;
  private work = new Map<string, QueryGroup>();
  private legend: LegendEntry[] = [];

  constructor(options: CollectionDataOptions = {}) {
    super(options.debug || false);

    this.baseUrl = options.baseUrl || COLLECTION_DATA_API;

    const params = new URLSearchParams(window.location.search);

    // `?__fresh__` on the page's own URL requests this window's data from
    // the API on every request this page makes.
    this.fresh = params.has('__fresh__');
    // `?__debug__` outlines every image a reference filled — blue one
    // level deep, red two — so a page can be read against the gallery
    // documents.
    this.outlines = params.has('__debug__');

    this.ready = this.init();
  }

  init(): Promise<void> {
    // The class rides the flag alone, so styles and people can see the
    // debug state whatever the page holds.
    if (this.outlines) document.body.classList.add('cms-debug');

    this.work = this.scan();

    if (!this.work.size) {
      this.log('No collection attribute is on the page. Component is inactive.');
      return Promise.resolve();
    }

    // An item query needs the slug its source names, and a
    // collection query carries its context instead. A group with neither
    // has nothing to stand on, and its elements keep their markup.
    this.work.forEach((group, key) => {
      if (group.slug || Object.keys(group.context).length) return;

      this.log(`No ${ITEM_ATTRIBUTE} declares ${group.source}. It was not requested.`);
      this.work.delete(key);
    });

    if (!this.work.size) return Promise.resolve();

    return this.load();
  }

  /**
   * The bindings of the page, grouped by source and by the context their
   * selectors resolved to. A path that names no field is dropped here, so
   * it costs no request — and so is a path whose context aliases no
   * ancestor declares.
   */
  private scan(): Map<string, QueryGroup> {
    const work = new Map<string, QueryGroup>();
    // Every context element on the page, read once — an element never
    // walks the tree to find its context.
    const lookup = buildContext();

    APPLIER_NAMES.forEach((applier) => {
      const attribute = attributeOf(applier);

      document.querySelectorAll<HTMLElement>(`[${attribute}]`).forEach((node) => {
        const raw = node.getAttribute(attribute) || '';
        // What the container holds at scan is what `[~]` stands for.
        const anchor = node.firstChild;

        entriesOf(applier, raw).forEach((entry) => {
          const key = readPath(entry.path);

          if (!key) {
            this.log(`This ${attribute} names no field:`, entry.path);
            return;
          }

          const context: Record<string, string> = {};
          let met = true;

          contextRefsOf(key.path).forEach((alias) => {
            const value = lookup(node, alias);

            if (value) context[alias] = value;
            else met = false;
          });

          if (!met) {
            this.log(
              `No ${CONTEXT_ATTRIBUTE} declares an alias in ${entry.path}. The element keeps its markup.`,
              node
            );
            return;
          }

          // The source is a context alias too: the nearest context element
          // that declares it names which item this path starts on.
          const slug = lookup(node, key.source) || '';
          const pairs = Object.keys(context)
            .sort()
            .map((alias) => `${alias}=${context[alias]}`);
          const groupKey = [key.source, slug, ...pairs].join('|');
          const group = work.get(groupKey) || { source: key.source, slug, context, bindings: [] };

          group.bindings.push({
            node,
            applier,
            path: key.path,
            ...(entry.before ? { anchor } : {})
          });
          work.set(groupKey, group);
        });
      });
    });

    return work;
  }

  /** One request per source-and-context group, however many elements read it. */
  private load = async (): Promise<void> => {
    this.log(`Requesting ${this.work.size} group(s).`);

    await Promise.all(Array.from(this.work.values(), (group) => this.applySource(group)));

    if (this.outlines) renderLegend(this.legend);
  };

  private async applySource(group: QueryGroup): Promise<void> {
    // An element requesting what another already requested rides along.
    const paths = Array.from(new Set(group.bindings.map((binding) => binding.path)));
    const started = performance.now();
    const payload = await this.fetchSource(group, paths);

    if (!payload) return;

    await this.apply(group, payload.values || {}, payload.meta || {});
    this.report(group, payload.unresolved || []);
    this.reportTiming(group, payload.timing, performance.now() - started);
  }

  private async fetchSource(
    group: QueryGroup,
    paths: string[]
  ): Promise<CollectionPayload | null> {
    const query = [`source=${encodeURIComponent(group.source)}`];

    if (group.slug) query.push(`slug=${encodeURIComponent(group.slug)}`);

    // The pairs travel exactly as the attribute wrote them.
    const pairs = Object.keys(group.context)
      .sort()
      .map((alias) => `${alias}=${group.context[alias]}`)
      .join(',');

    if (pairs) query.push(`context=${encodeURIComponent(pairs)}`);

    query.push(`cmsPaths=${paths.map(encodeURIComponent).join(',')}`);

    if (this.fresh) query.push('fresh');

    const queryString = query.join('&');

    try {
      const response = await fetch(`${this.baseUrl}?${queryString}`);

      if (!response.ok) {
        this.log(`The source ${group.source} responded`, response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      this.log(`The source ${group.source} did not respond.`, error);
      return null;
    }
  }

  /**
   * Write every value in. An image applier returns a promise — the
   * file loads before the swap — so this settles when the last one lands.
   */
  private async apply(
    group: QueryGroup,
    values: CmsResults,
    meta: Record<string, PathMeta> = {}
  ): Promise<void> {
    const { bindings } = group;
    const settling: Promise<void>[] = [];
    let applied = 0;

    bindings.forEach(({ node, applier, path, anchor }) => {
      const value = values[path];

      if (isEmpty(value)) {
        this.log(`${path} is empty. The element keeps what it has.`);
        return;
      }

      const highlight = this.outlines ? highlightFor(path) : undefined;
      const outcome = APPLIERS[applier](node, value, anchor, highlight);

      // The legend lists images alone — a text value wears no highlight
      // either, however deep its path runs.
      if (highlight && applier !== 'text') this.collectLegend(path, meta[path]);

      if (outcome) settling.push(outcome);
      applied += 1;
    });

    await Promise.all(settling);

    this.log(`Applied ${applied} of ${bindings.length} ${group.source} element(s).`);
  }

  /**
   * One legend entry per image a reference filled. An API without `meta`
   * sends nothing here, and the legend simply stays empty.
   */
  private collectLegend(path: string, info?: PathMeta): void {
    if (!info || !info.itemName || !info.fieldName) {
      this.log(`No meta came for ${path}. The legend skips it.`);
      return;
    }

    this.legend.push({
      itemName: info.itemName,
      fieldName: info.fieldName,
      depth: depthOf(path)
    });
  }

  /**
   * One line per request, under `?__debug__`.
   *
   * `applied` is the wait the page actually had: it starts when the
   * request goes out and ends when the last value is in the markup, which
   * is the one number the network panel cannot show. The rest comes from
   * the API, and names how much of its time was Webflow and how many
   * reads a cached copy served.
   *
   * A response the browser served from its own cache carries the timing of
   * the request that filled it.
   */
  private reportTiming(group: QueryGroup, timing: Timing | undefined, applied: number): void {
    if (!this.outlines) return;

    const parts = [`collection-data · ${group.source}`, `${Math.round(applied)}ms applied`];

    if (timing) {
      parts.push(
        `${timing.ms}ms api`,
        `${timing.webflowMs}ms webflow`,
        `${timing.reads} read(s), ${timing.hits} cached`
      );
    }

    console.info(parts.join(' · '));
  }

  /**
   * Name what the API could not resolve, and how many elements were waiting on
   * it. `at` is the segment the walk stopped on, which is usually the typo.
   */
  private report(group: QueryGroup, unresolved: Unresolved[]): void {
    if (!this.debug || !unresolved.length) return;

    unresolved.forEach(({ path, reason, at }) => {
      const waiting = group.bindings.filter((binding) => binding.path === path);

      this.log(
        `Unresolved: ${path} (${reason}${at ? ` at "${at}"` : ''}).`,
        `${waiting.length} element(s) kept their own markup.`,
        waiting.map((binding) => binding.node)
      );
    });
  }
}
